import { useIntl } from '@/intl'
import { Card } from '@/scopes/common/components/Card'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { getAccommodationServices } from '@/scopes/search/search/domain/product-card/transformIncludedServices'
import { Campaign } from '@/scopes/search/search/infrastructure/api/types'
import * as List from '@/scopes/search/search/pages/SearchPage/components/Results/ListItem'
import { CampaignUsp } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/CampaignUsp'
import {
  ProductCardProps,
} from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCard'
import { Price } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardSplitDetails/Price'
import { UserRating } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ProductCardSplitDetails/UserRating'
import { AccommodationService } from '@/scopes/search/search/pages/SearchPage/components/Results/Result'
import { useDecision } from '@optimizely/react-sdk'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, Rating, Tag, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { Collection, CollectionTag } from '../CollectionTag'
import FavoriteButton from '../FavoriteButton'
import { ImageCarousel } from '../ImageCarousel'
import { LocationTag } from '../LocationTag'
import { OverlayLink } from '../OverlayLink'

const LIST_ITEM_ICON_SIZE = 16

const StyledProductCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: ${tok('semantics_content_color')};
  position: relative;

  @media (min-width: 800px){
    flex-direction: row;
  }
  ${focusOutline()}
  ${ImageCarousel.modPartialImages(true)}
`

const Media = styled.div`
  display: flex;
  padding-top: ${space('primitives_space_12')};
  width: 100%;
  height: 170px;
  align-items: flex-start;
  flex-shrink: 0;

  > ${Tag} {
    left: ${space('primitives_space_12')};
    position: absolute;
    pointer-events: none;
    top: ${space('primitives_space_12')};
    max-width: 75%;

    :last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  ${FavoriteButton} {
    position: absolute;
    top: ${space('primitives_space_16')};
    right: ${space('primitives_space_16')};
  }

  @media (hover: hover) {
    &:hover {
      button {
        display: inline-flex;
      }
    }
  }
`

ProductCardSplitDetails.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`
        ${Media} {
          width: 240px;
          height: 362px;
          padding-top: initial;
        }
        ${ImageCarousel.modPartialImages(false)}
      `
    case 'desktop':
      return css`
        ${Media} {
          width: 320px;
          height: 362px;
        }
        ${ImageCarousel.modPartialImages(false)}
      `
  }
}

const StyledCollectionTag = styled(CollectionTag)`
  position: absolute;
  top: ${space('primitives_space_12')};
  left: ${space('primitives_space_12')};
  pointer-events: none;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${space('primitives_space_6')}  ${space('primitives_space_6')}  ${space('primitives_space_6')};
  align-items: flex-start;
  gap: ${space('primitives_space_16')};
  flex: 1 0 0;

  @media (min-width: 800px){
    flex-direction: row;
    padding: ${space('primitives_space_12')}  ${space('primitives_space_12')}  ${space('primitives_space_12')}  ${space('primitives_space_16')};
  }

  ${focusOutline()}
`

const Main = styled.div`
  display: flex;
  padding: ${space('primitives_space_6')};
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;
  gap: ${space('primitives_space_16')};
`

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const UspList = styled(List.Root)`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: ${space('primitives_space_6')} ${space('primitives_space_6')};
  align-self: stretch;
  flex-wrap: wrap;

  @media (min-width: 800px){
    gap: ${space('primitives_space_6')};
    flex-direction: column;
  }
`

const Item = styled(List.Item)`
  align-items: center;
`

const AccoName = styled('Typography')`
  ${Typography.modVariant('display-i-regular')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-g-regular')};
  }
`
const Configuration = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`

const ConfigurationItem = styled.div`
  display: flex;
  padding: ${space('primitives_space_4')} 0;
  gap: ${space('primitives_space_12')};
  align-items: center;
  color: ${tok('semantics_content_color')};
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  gap: ${space('primitives_space_8')};
  align-self: stretch;
  flex-wrap: wrap;

  ${FavoriteButton} {
    z-index: 1;
  }
`
const ViewButton = styled(Button)`
  flex: 1 0 0;
  width: 100%;
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    width: unset;
  }
`

const Aside = styled.div`
  display: flex;
  padding: ${space('primitives_space_16')};
  flex-wrap: wrap;
  border-radius: ${tokPx('primitives_corner_radius_m')};
  background: ${tok('semantics_views_tinted_background_color')};
  justify-content: space-between;
  width: 100%;

  ${Configuration} {
    flex: 0 0 auto;
    order: 0;
  }
  ${Price} {
    flex: 0 0 auto;
    order: 1;
  }
  ${Actions} {
    flex-basis: 100%;
    order: 2;
    margin-top: 16px;
  }

  @media (min-width: 800px){
    width: 240px;
    flex-direction: column;
    align-items: center;
    gap: ${space('primitives_space_16')};

    ${Configuration} {
      order: 1;
    }
    ${Price} {
      order: 0;
    }
    ${Actions} {
      margin-top: initial;
    }
  }
`

export function ProductCardSplitDetails({ result, participants, onClick }: ProductCardProps): JSX.Element {
  const {
    id: accommodationId,
    price,
    urlWithFilters: href,
    images: imageSources,
    locations,
    name = '',
    rating = 0,
    userRating,
    userRatingText,
    collection,
    isMarketExclusive,
    isSustainable,
    campaign,
    usps,
    outboundDepartureAirportName,
    arrivalDateDetail: arrivalDate,
    departureDateDetail: departureDate,
    featuredFilters,
  } = result
  const intl = useIntl()
  const { isDesktopSm } = useBreakpoints()
  const translations = {
    free: intl.formatMessage({ id: 'product_card_ski_free' }),
    skimaterial: intl.formatMessage({ id: 'product_card_ski_skimaterial' }),
    skipass: intl.formatMessage({ id: 'product_card_ski_skipass' }),
    and: intl.formatMessage({ id: 'product_card_ski_and' }),
    skipassPrice: intl.formatMessage({ id: 'product_card_ski_skipass_price' }),
  }
  const priceFormatter = intl.formatTruncatedCurrency
  const accommodationServices = getAccommodationServices(translations, priceFormatter, price)
  const departureDateRaw = price?.acmInformation?.departureDateRaw
  const durationRaw = price?.acmInformation?.durationRaw
  const yieldCampaign = price?.yieldCampaignName
  const averagePrice = price?.averagePrice
  const transportType = price?.acmInformation?.transportType
  const priceDiscounts = price?.priceDiscounts
  const partyComposition = featuredFilters?.occupancy
  const mealPlan = featuredFilters?.mealplan
  const collectionType = (collection?.id.toLowerCase() || 'default') as Collection
  // const [{ enabled: showCTA }] = useDecision('ID_385_bring_back_the_button_in_the_cards_on_plp')
  const [{ enabled: durationInsteadDate }] = useDecision('ID_412_show_duration_instead_of_end_date')

  const getLimitedListedUsps = (
    isMarketExclusive: boolean,
    isSustainable: boolean,
    campaign: Campaign | undefined,
    accommodationServices: AccommodationService[],
    usps: string[],
  ): React.ReactNode[] => {
    const maxUsps = 6
    const result: React.ReactNode[] = []
    let count = 0

    if (isMarketExclusive && count < maxUsps) {
      result.push(
        <Item $icon="LogoSunweb" $size={LIST_ITEM_ICON_SIZE} data-qa="sunweb-only-usp" key="sunweb-only-usp">
          <Typography $variant="body-s-regular-tight">
            {intl.formatMessage({ id: 'product_card_sunweb_exclusive' })}
          </Typography>
        </Item>,
      )
      count++
    }

    if (isSustainable && count < maxUsps) {
      result.push(
        <Item $icon="Leaf" $size={LIST_ITEM_ICON_SIZE} data-qa="sustainability-usp" key="sustainability-usp">
          <Typography $variant="body-s-regular-tight">
            {intl.formatMessage({ id: 'product_card_sustainable' })}
          </Typography>
        </Item>,
      )
      count++
    }

    result.push(<CampaignUsp campaign={campaign} />)
    count++

    for (const service of accommodationServices) {
      if (count >= maxUsps)
        break
      result.push(
        <Item $icon={service.icon} key={service.key} $size={LIST_ITEM_ICON_SIZE}>
          <Typography $variant="body-s-regular-tight">{service.text}</Typography>
        </Item>,
      )
      count++
    }

    for (let i = 0; i < usps.length && count < maxUsps; i++) {
      result.push(
        <Item data-qa="acco-usp" $size={LIST_ITEM_ICON_SIZE} key={`acco-usp${i}`}>
          <Typography $variant="body-s-regular-tight">{usps[i]}</Typography>
        </Item>,
      )
      count++
    }

    return result
  }

  const listedUsps = getLimitedListedUsps(isMarketExclusive, isSustainable, campaign, accommodationServices, usps)

  return (
    <StyledProductCard qa-product-card="true" data-qa="product-card" onClick={onClick}>
      {/* OverlayLink is intentionally empty, because the whole card should be clickable, but also the image carousel should be scrollable & buttons clickable */}
      <OverlayLink href={href} aria-label={name} target={isDesktopSm ? '_blank' : '_self'} />
      <Media>
        <ImageCarousel
          imageIndex={accommodationId}
          images={imageSources.map(imageSrc => ({
            src: imageSrc,
            alt: name,
          }))}
          href={href}
        />
        {yieldCampaign
          ? (
              <Tag variant="primary">{yieldCampaign}</Tag>
            )
          : (
              collection && <StyledCollectionTag collectionName={collectionType}>{collection.name}</StyledCollectionTag>
            )}
      </Media>
      <Content>
        <Main>
          <HeaderGroup>
            <Rating rating={rating} />
            <AccoName as="span">{name}</AccoName>
            <LocationTag locations={locations} />
          </HeaderGroup>
          <UserRating userRating={userRating} aria-label={userRatingText} />
          <UspList>{listedUsps}</UspList>
        </Main>
        <Aside>
          {averagePrice && (
            <Price
              averagePrice={averagePrice}
              discounts={priceDiscounts}
              accommodationId={accommodationId}
              departureDateRaw={departureDateRaw}
              durationRaw={durationRaw}
              mealPlan={mealPlan}
              transportType={transportType}
              participants={participants}
            />
          )}
          <Configuration>
            <ConfigurationItem>
              <Icon glyph="Family" size={20} />
              <Typography as="div" $variant="body-s-regular-tight">
                {partyComposition}
              </Typography>
            </ConfigurationItem>
            <ConfigurationItem>
              <Icon glyph="Calendar" size={20} />
              <Typography as="div" $variant="body-s-regular-tight">
                {durationInsteadDate
                  ? `${departureDate} | ${featuredFilters?.duration || ''}`
                  : `${departureDate} - ${arrivalDate}`}
              </Typography>
            </ConfigurationItem>
            {outboundDepartureAirportName && (
              <ConfigurationItem>
                <Icon glyph="AirplaneTakingOff" size={20} />
                <Typography as="div" $variant="body-s-regular-tight">
                  {outboundDepartureAirportName}
                </Typography>
              </ConfigurationItem>
            )}
            <ConfigurationItem>
              <Icon glyph="ForkAndKnive" size={20} />
              <Typography as="div" $variant="body-s-regular-tight">
                {mealPlan}
              </Typography>
            </ConfigurationItem>
          </Configuration>
          <Actions>
            <FavoriteButton productId={accommodationId} />
            <ViewButton>{intl.formatMessage({ id: 'product_card_view_button_text' })}</ViewButton>
          </Actions>
        </Aside>
      </Content>
    </StyledProductCard>
  )
}

ProductCardSplitDetails.displayName = 'ProductCardVariant417'
